import { useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import type { GeocodeSuggestion } from '@pms/shared';
import { Button } from '@/components/ui/Button';
import { usePropertyEditorApi } from './api-context';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import type { PcPropertyCore } from '@/services/api/property-content.api';
import { SectionCard, TextField, TextAreaField, SelectField } from './fields';
import { LocationSearchInput } from './LocationSearchInput';

const CANCELLATION_OPTIONS = [
  { value: 'flexible', label: 'Flexible' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'strict', label: 'Strict' },
  { value: 'non_refundable', label: 'Non-refundable' },
];
const PET_OPTIONS = [
  { value: 'allowed', label: 'Allowed' },
  { value: 'not_allowed', label: 'Not allowed' },
];
const SMOKING_OPTIONS = [
  { value: 'allowed', label: 'Allowed' },
  { value: 'not_allowed', label: 'Not allowed' },
];

interface CoreForm {
  name: string;
  description: string;
  tagLine: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  latitude: number | null;
  longitude: number | null;
  currency: string;
  website: string;
  checkInTime: string;
  checkOutTime: string;
  cancellationPolicy: string;
  petPolicy: string;
  smokingPolicy: string;
  houseRules: string;
}

function toForm(p: PcPropertyCore): CoreForm {
  return {
    name: p.name ?? '',
    description: p.description ?? '',
    tagLine: p.tagLine ?? '',
    address: p.address ?? '',
    city: p.city ?? '',
    state: p.state ?? '',
    country: p.country ?? '',
    zipCode: p.zipCode ?? '',
    latitude: p.latitude ?? null,
    longitude: p.longitude ?? null,
    currency: p.currency ?? 'USD',
    website: p.website ?? '',
    checkInTime: p.checkInTime ?? '',
    checkOutTime: p.checkOutTime ?? '',
    cancellationPolicy: p.cancellationPolicy ?? 'flexible',
    petPolicy: p.petPolicy ?? 'not_allowed',
    smokingPolicy: p.smokingPolicy ?? 'not_allowed',
    houseRules: p.houseRules ?? '',
  };
}

export function OverviewTab({ property }: { property: PcPropertyCore }) {
  const initial = useMemo(() => toForm(property), [property]);
  const [form, setForm] = useState<CoreForm>(initial);
  const [error, setError] = useState('');
  const { useUpdatePropertyCore } = usePropertyEditorApi();
  const mutation = useUpdatePropertyCore(property.id);

  const isDirty = useMemo(
    () => (Object.keys(form) as (keyof CoreForm)[]).some((k) => form[k] !== initial[k]),
    [form, initial],
  );
  useUnsavedGuard(isDirty);

  const set = <K extends keyof CoreForm>(key: K, value: CoreForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Autocomplete selection populates the address block + hidden coordinates.
  // A bare POI/street with no house number falls back to the place name (D#2).
  function handleLocationSelect(s: GeocodeSuggestion) {
    const addr = s.street ? `${s.houseNumber ?? ''} ${s.street}`.trim() : s.name;
    setForm((f) => ({
      ...f,
      address: addr || f.address,
      city: s.city ?? f.city,
      state: s.state ?? f.state,
      country: s.country ?? f.country,
      zipCode: s.postcode ?? f.zipCode,
      latitude: s.lat,
      longitude: s.lon,
    }));
  }

  const hasCoords = form.latitude != null && form.longitude != null;

  const trimmedCurrency = form.currency.trim().toUpperCase();
  const isValid =
    form.name.trim() !== '' &&
    form.address.trim() !== '' &&
    form.city.trim() !== '' &&
    form.country.trim() !== '' &&
    trimmedCurrency.length === 3;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!isValid) {
      setError('Name, address, city, country and a 3-letter currency are required.');
      return;
    }
    try {
      await mutation.mutateAsync({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        tagLine: form.tagLine.trim() || undefined,
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim() || undefined,
        country: form.country.trim(),
        zipCode: form.zipCode.trim() || undefined,
        latitude: form.latitude ?? undefined,
        longitude: form.longitude ?? undefined,
        currency: trimmedCurrency,
        website: form.website.trim() || undefined,
        checkInTime: form.checkInTime || undefined,
        checkOutTime: form.checkOutTime || undefined,
        cancellationPolicy: form.cancellationPolicy,
        petPolicy: form.petPolicy,
        smokingPolicy: form.smokingPolicy,
        houseRules: form.houseRules.trim() || undefined,
      });
      showToast('Overview saved · live on storefront');
    } catch (err) {
      setError(extractErrorMessage(err));
      showToast('Could not save overview', 'error');
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      <SectionCard title="General">
        <TextField id="ov-name" label="Property name" value={form.name} onChange={(v) => set('name', v)} required maxLength={255} />
        <TextField id="ov-tagline" label="Tagline" value={form.tagLine} onChange={(v) => set('tagLine', v)} maxLength={255} placeholder="A short headline shown on the storefront" />
        <TextAreaField id="ov-desc" label="Description" value={form.description} onChange={(v) => set('description', v)} rows={4} maxLength={5000} />
        <TextField id="ov-website" label="Website" value={form.website} onChange={(v) => set('website', v)} type="url" inputMode="url" maxLength={255} placeholder="https://…" />
      </SectionCard>

      <SectionCard title="Location">
        <LocationSearchInput
          id="ov-address"
          label="Address"
          value={form.address}
          onChange={(v) => set('address', v)}
          onSelect={handleLocationSelect}
          required
          maxLength={500}
          placeholder="Start typing an address or place…"
          hint={
            hasCoords
              ? `Coordinates captured ✓ (${form.latitude!.toFixed(5)}, ${form.longitude!.toFixed(5)})`
              : 'Pick a suggestion to capture map coordinates.'
          }
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField id="ov-city" label="City" value={form.city} onChange={(v) => set('city', v)} required maxLength={100} />
          <TextField id="ov-state" label="State / region" value={form.state} onChange={(v) => set('state', v)} maxLength={100} />
          <TextField id="ov-country" label="Country" value={form.country} onChange={(v) => set('country', v)} required maxLength={100} />
          <TextField id="ov-zip" label="Zip / postcode" value={form.zipCode} onChange={(v) => set('zipCode', v)} maxLength={20} />
          <TextField id="ov-currency" label="Currency" value={form.currency} onChange={(v) => set('currency', v.toUpperCase())} maxLength={3} hint="3-letter ISO code" className="max-w-[160px]" />
        </div>
      </SectionCard>

      <SectionCard title="Policies" description="Shown to guests at booking time.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField id="ov-checkin" label="Check-in time" value={form.checkInTime} onChange={(v) => set('checkInTime', v)} type="time" />
          <TextField id="ov-checkout" label="Check-out time" value={form.checkOutTime} onChange={(v) => set('checkOutTime', v)} type="time" />
          <SelectField id="ov-cancellation" label="Cancellation policy" value={form.cancellationPolicy} onChange={(v) => set('cancellationPolicy', v)} options={CANCELLATION_OPTIONS} />
          <SelectField id="ov-pet" label="Pet policy" value={form.petPolicy} onChange={(v) => set('petPolicy', v)} options={PET_OPTIONS} />
          <SelectField id="ov-smoking" label="Smoking policy" value={form.smokingPolicy} onChange={(v) => set('smokingPolicy', v)} options={SMOKING_OPTIONS} />
        </div>
        <TextAreaField id="ov-house-rules" label="House rules" value={form.houseRules} onChange={(v) => set('houseRules', v)} rows={4} maxLength={10000} />
      </SectionCard>

      <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white/90 py-3 backdrop-blur">
        <span className="text-sm text-gray-500" aria-live="polite">
          {isDirty ? 'You have unsaved changes.' : 'All changes saved.'}
        </span>
        <Button type="submit" isLoading={mutation.isPending} disabled={!isDirty || !isValid || mutation.isPending}>
          <Save size={16} aria-hidden="true" />
          Save changes
        </Button>
      </div>
    </form>
  );
}
